import { NegotiatedebitService } from '../../../services/negotiatedebit/negotiatedebit.service';
import { Component, OnInit } from '@angular/core';
import moment from 'moment';
import { SelectItem } from 'primeng/api';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { Customer } from 'src/app/models/customer';
import { FinancesService } from 'src/app/services/finances.service';
import { InvoiceService } from 'src/app/services/invoice.service';
import { SlingConfigService } from 'src/app/services/sling-config.service';
import { MessageUtil } from 'src/app/utils/message.util';
import { NegotiateCreateComponent } from './negotiate-create/negotiate-create.component';
import { PaginationFilter } from 'src/app/models/pagination-filter';
import { BilletDialogComponent } from '../billets-management/billet/billet-dialog/billet-dialog.component';
import { NegotiateDebtInvoice } from '../../../models/negotiatedebit/negotiatedebt-invoice';
@Component({
  selector: 'app-negotiate-debt',
  templateUrl: './negotiate-debt.component.html',
  styleUrls: ['./negotiate-debt.component.scss'],
})
export class NegotiateDebtComponent implements OnInit {
  private readonly oneDay: number = 1000 * 60 * 60 * 24;
  private carteiras: any[] = [];
  invoices: NegotiateDebtInvoice[] = [];
  selectedinvoices: NegotiateDebtInvoice[] = [];
  selectedAmount: number = 0;
  slingConfig: any = null;
  customers: SelectItem[] = [{ label: 'Select Customer', value: null }];

  selectedCustomer: Customer = null;
  headerCheckboxChecked: boolean = false;

  toleranceDays = 0;
  taxaMulta = 0.0;
  taxaMora = 0.0;

  totalPending = 0;

  constructor(
    public dialogRef: DynamicDialogRef,
    private messageUtil: MessageUtil,
    private dialog: DialogService,
    private config: DynamicDialogConfig,
    private invoiceService: InvoiceService,
    private negotiateDebitService: NegotiatedebitService,
    private financesService: FinancesService,
    private slingConfigService: SlingConfigService
  ) {}

  async ngOnInit() {
    await this.loadSlingConfig();
    await this.loadAllBillingPortifolio();
    this.loadCustomers();
  }

  loadSlingConfig(): Promise<void> {
    return this.slingConfigService
      .getSlingConfig()
      .toPromise()
      .then((data) => {
        this.slingConfig = data[0];
      });
  }

  loadAllBillingPortifolio(): Promise<any[]> {
    return new Promise<any[]>(async (resolve) => {
      await this.financesService.getAllBilingPortifolio().subscribe(
        (data) => {
          this.carteiras = data;
          resolve(data);
        },
        (error) => {
          console.log(error);
        }
      );
    });
  }

  loadCustomers() {
    this.negotiateDebitService.getCustomers().subscribe({
      next: (r) => {
        this.customers = r.map((customer) => ({
          label: customer.name + ' (' + customer.totalPending + ')',
          value: customer,
        }));

        if (this.config.data.customer) {
          this.selectedCustomer = this.config.data.customer;
          this.loadInvoices();
        }
      },

      error: (error) => {
        this.messageUtil.generateMessage(
          'error',
          'Error loading customers',
          error
        );
      },
    });
  }

  loadInvoices() {
    this.invoiceService.getDebits(this.selectedCustomer.id).subscribe({
      next: (invoices: NegotiateDebtInvoice[]) => {
        this.modifyListDateOrders(invoices);
        this.getTotals(invoices);

        this.invoices = (invoices || []).map((inv) => ({
          ...inv,
          selectionDisabled: this.hasOpenBankingBillets(inv?.bankingBilletList),
        }));
        this.selectedinvoices = [];
        this.selectedAmount = 0;
        this.headerCheckboxChecked = false;
      },
      error: (error) => {
        this.messageUtil.generateMessage(
          'error',
          'Error loading invoices',
          error
        );
      },
    });
  }

  private hasOpenBankingBillets(billets?: unknown[] | null): boolean {
    if (!billets || billets.length === 0) return false;
    return billets.some(
      (b: any) => b?.bankCanceled === false || b?.bankCanceled === null
    );
  }

  areAllInvoicesDisabled(): boolean {
    if (!this.invoices || this.invoices.length === 0) {
      return true;
    }
    return this.invoices.every((invoice) => invoice.selectionDisabled === true);
  }

  getEnabledInvoices(): NegotiateDebtInvoice[] {
    if (!this.invoices) {
      return [];
    }
    return this.invoices.filter((invoice) => invoice.selectionDisabled !== true);
  }

  onHeaderCheckboxToggle(): void {
    const enabledInvoices = this.getEnabledInvoices();

    if (this.headerCheckboxChecked) {
      this.selectedinvoices = [...enabledInvoices];
    } else {
      this.selectedinvoices = [];
    }
  }

  updateHeaderCheckboxState(): void {
    const enabledInvoices = this.getEnabledInvoices();

    if (enabledInvoices.length === 0) {
      this.headerCheckboxChecked = false;
      return;
    }
    this.headerCheckboxChecked = enabledInvoices.every(invoice =>
      this.selectedinvoices.some(selected => selected === invoice || selected.id === invoice.id)
    );
  }

  openBilletsModal(invoice): void {
      const paginationFilter = new PaginationFilter();
      paginationFilter.size = 7;
      this.dialog.open(BilletDialogComponent, {
        data: {
          filters: false,
          filter: {
            invoiceNumberStart: invoice.invoiceNumber,
            invoiceNumberEnd: invoice.invoiceNumber
          },
          numberOfRows: 7,
          paginationFilter,
          invoice
        },
        header: 'Boletos da fatura ' + invoice.invoiceNumber
      }).onClose.subscribe(() => {
        this.loadInvoices();
      });
    }

  modifyListDateOrders(invoices: NegotiateDebtInvoice[]): void {
    const now: number = moment().toDate().getTime();

    invoices.forEach((invoice) => {
      const dueDateTime = new Date(invoice.dueDate).getTime();

      // If it's not due yet, there are no penalties/interest for this screen
      if (dueDateTime > now) {
        invoice.interest = 0;
        invoice.multa = 0;
        invoice.mora = 0;
        return;
      }

      invoice.interest = this.calcInterest(invoice);
      invoice.multa = this.calcMulta(this.daysPastDueDate(invoice), invoice);
      invoice.mora = this.calcMora(
        this.daysPastDueDate(invoice) - this.getToleranceDays(invoice),
        invoice
      );

      invoice.interest !== 0
        ? (invoice.interest =
            Math.round((invoice.interest + Number.EPSILON) * 100) / 100)
        : (invoice.interest = 0);
      invoice.multa !== 0
        ? (invoice.multa =
            Math.round((invoice.multa + Number.EPSILON) * 100) / 100)
        : (invoice.multa = 0);
      invoice.mora !== 0
        ? (invoice.mora =
            Math.round((invoice.mora + Number.EPSILON) * 100) / 100)
        : (invoice.mora = 0);
    });
  }

  getDays(dueDate: Date): string {
    if (!dueDate) return '';

    const today = moment();
    const due = moment(dueDate);
    const diff = due.diff(today, 'days');

    if (diff > 0) {
      return `Faltam ${diff} dias`;
    } else if (diff < 0) {
      return `Vencido há ${Math.abs(diff)} dias`;
    } else {
      return 'Vence hoje';
    }
  }

  getDaysDueDate(now: number, dueDate: number): number {
    const calc: number = (now - dueDate) / this.oneDay;
    return Number(calc.toFixed(0));
  }

  getTotals(invoices: Array<{ value: number }>): void {
    this.totalPending = invoices.reduce(
      (total, invoice) => total + invoice.value,
      0
    );
  }

  getTotalInterest(invoices) {
    let totalInterest = 0;
    if (invoices) {
      invoices.forEach((invoice) => {
        totalInterest += this.calcInterest(invoice);
      });
    }
    return totalInterest;
  }

  getTotal(invoices, totalCustomer) {
    return this.getTotalInterest(invoices) + totalCustomer;
  }

  public calcInterest(invoice): number {
    const daysPastDue = this.daysPastDueDate(invoice);

    let allInterest = 0;

    if (daysPastDue <= 0) {
      return allInterest;
    }

    allInterest += this.calcMulta(daysPastDue, invoice);

    if (daysPastDue > this.getToleranceDays(invoice)) {
      const daysMora = daysPastDue - this.getToleranceDays(invoice);
      allInterest += this.calcMora(daysMora, invoice);
    }
    return allInterest;
  }

  private getMulta(invoice) {
    let conta = this.carteiras.find(
      (d) => d.conta.idConta === invoice.accountId
    );
    if (!conta) {
      conta = this.carteiras.find(
        (d) => d.conta.idConta === this.slingConfig.accountId
      );
    }
    if (conta) {
      return conta?.taxaMulta;
    }
    return 0;
  }

  private getMora(invoice) {
    let conta = this.carteiras.find(
      (d) => d.conta.idConta === invoice.accountId
    );
    if (!conta) {
      conta = this.carteiras.find(
        (d) => d.conta.idConta === this.slingConfig.accountId
      );
    }
    if (conta) {
      return conta?.taxaMora;
    }
    return 0;
  }

  private getToleranceDays(invoice) {
    let conta = this.carteiras.find(
      (d) => d.conta.idConta === invoice.accountId
    );
    if (!conta) {
      conta = this.carteiras.find(
        (d) => d.conta.idConta === this.slingConfig.accountId
      );
    }
    if (conta) {
      return conta?.diasMulta;
    }
    return 0;
  }

  private calcMulta(daysPastDue, invoice): number {
    return parseFloat(
      ((parseFloat(invoice.value) * this.getMulta(invoice)) / 100.0).toFixed(2)
    );
  }

  private calcMora(daysMora: number, invoice): number {
    const taxaDay = parseFloat((this.getMora(invoice) / 30).toFixed(4));
    //tem de arredondar aqui para 2 se não o valor fica errado comparando com o banco
    const valueToDay =
      Math.ceil(((parseFloat(invoice.value) * taxaDay) / 100) * 100) / 100;

    return daysMora > 0 ? parseFloat((valueToDay * daysMora).toFixed(2)) : 0;
  }

  private daysPastDueDate(invoice): number {
    const dueDate = moment(invoice.dueDate);

    const today = moment();
    const due = moment(dueDate);
    const diff = due.diff(today, 'days');

    return Math.abs(diff);
  }

  getTotalPending(): number {
    return this.invoices.reduce(
      (total, invoice) => total + invoice.value,
      0
    );
  }

  getTotalPendingMoraAndMulta(): number {
    return this.invoices.reduce(
      (total, invoice) =>
        total +
        this.calcMulta(this.daysPastDueDate(invoice), invoice) +
        this.calcMora(
          this.daysPastDueDate(invoice) - this.getToleranceDays(invoice),
          invoice
        ),
      0
    );
  }

  getTotalNegotiate(): number {
    return this.selectedinvoices.reduce(
      (total, invoice) =>
        total +
        invoice.value +
        this.calcMulta(this.daysPastDueDate(invoice), invoice) +
        this.calcMora(
          this.daysPastDueDate(invoice) - this.getToleranceDays(invoice),
          invoice
        ),
      0
    );
  }

  negotiate(){
      const data = { table: this.selectedinvoices, customer: this.selectedCustomer };
        this.dialog.open(NegotiateCreateComponent, {
          width: '50%',
          height: '90vh',
          dismissableMask: false,
          data,
          header: "Finalizar Negociação",
        })
        .onClose.subscribe(() => {
          this.cleanEverything();
        });
  }

  cleanEverything() {
    this.invoices = [];
    this.selectedinvoices = [];
    this.selectedAmount = 0;
    this.totalPending = 0;
    this.selectedCustomer = null;
  }
}
